/**
 * A stable, colourful avatar background derived from who the person is — the
 * way Vercel paints an account with no picture. The same seed (an email, an
 * id) always yields the same two hues, so an avatar is recognisable across
 * sessions and machines without storing anything.
 */

/** FNV-1a, 32 bit: tiny, dependency-free and evenly spread for short strings. */
export function hashSeed(seed: string): number {
  let hash = 0x811c9dc5;
  for (let index = 0; index < seed.length; index += 1) {
    hash ^= seed.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

/** The two hues of the gradient, 0–359, always far enough apart to read as two colours. */
export function avatarHues(seed: string): readonly [number, number] {
  const hash = hashSeed(seed.trim().toLowerCase());
  const first = hash % 360;
  // 60°–179° away: distinct, never the muddy near-complement wrap to itself.
  const second = (first + 60 + ((hash >>> 9) % 120)) % 360;
  return [first, second];
}

/** A CSS `background` value. Lightness is fixed so white initials always read. */
export function avatarGradient(seed: string): string {
  const [first, second] = avatarHues(seed);
  return `linear-gradient(135deg, oklch(0.68 0.19 ${first}), oklch(0.58 0.21 ${second}))`;
}
